import axios from "axios";
import Cookies from "js-cookie";
import {
  getShipmentSearchOfficeHeader,
  parseOfficeCodes,
  resolveClaimOfficeCode,
} from "../claimsOfficeUtils";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use(
  (config) => {
    const authToken = Cookies.get("authToken");
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    if (config.data instanceof FormData) {
      if (typeof config.headers.delete === "function") {
        config.headers.delete("Content-Type");
      } else {
        delete config.headers["Content-Type"];
      }
    }
    if (!config.headers["Office-Code"]) {
      const codes = parseOfficeCodes();
      if (codes.length === 1) {
        config.headers["Office-Code"] = codes[0];
      } else if (codes.length > 1) {
        config.headers["Office-Code"] = codes.join(",");
      }
    }
    return config;
  },
  (err) => Promise.reject(err)
);

const claimOfficeHeader = (officeCode) => {
  const resolved = resolveClaimOfficeCode({ officeCode });
  return resolved ? { headers: { "Office-Code": resolved } } : {};
};

const compareClaimSortValues = (a, b, sortBy, ascending) => {
  const key = sortBy || "dateFiled";
  const aVal = a?.[key];
  const bVal = b?.[key];

  const aEmpty = aVal == null || aVal === "";
  const bEmpty = bVal == null || bVal === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof aVal === "number" && typeof bVal === "number") {
    return ascending ? aVal - bVal : bVal - aVal;
  }

  const cmp = String(aVal).localeCompare(String(bVal), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return ascending ? cmp : -cmp;
};

const mergeClaimPages = (pages, codes, { sortBy = "dateFiled", ascending = false, page = 0, size = 50 } = {}) => {
  const byClaimNumber = new Map();
  pages.forEach((pageResult, index) => {
    const officeCode = codes[index];
    (pageResult?.content || []).forEach((claim) => {
      if (claim?.claimNumber) {
        byClaimNumber.set(claim.claimNumber, { ...claim, officeCode });
      }
    });
  });

  const sorted = Array.from(byClaimNumber.values()).sort((a, b) =>
    compareClaimSortValues(a, b, sortBy, ascending)
  );
  const totalElements = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / size) || 1);
  const start = Math.max(0, page) * size;
  const content = sorted.slice(start, start + size);

  return {
    content,
    totalElements,
    totalPages,
  };
};

const aggregateSummaries = (summaries) => {
  if (!summaries.length) {
    return {
      openClaimCount: 0,
      pendingPaymentTotal: 0,
      paidLast90DaysTotal: 0,
      avgDaysToClose: null,
    };
  }

  let openClaimCount = 0;
  let pendingPaymentTotal = 0;
  let paidLast90DaysTotal = 0;
  const avgValues = [];

  summaries.forEach((summary) => {
    if (!summary) return;
    openClaimCount += Number(summary.openClaimCount || 0);
    pendingPaymentTotal += Number(summary.pendingPaymentTotal || 0);
    paidLast90DaysTotal += Number(summary.paidLast90DaysTotal || 0);
    if (summary.avgDaysToClose != null) {
      avgValues.push(Number(summary.avgDaysToClose));
    }
  });

  return {
    openClaimCount,
    pendingPaymentTotal,
    paidLast90DaysTotal,
    avgDaysToClose:
      avgValues.length > 0
        ? Number((avgValues.reduce((a, b) => a + b, 0) / avgValues.length).toFixed(1))
        : null,
  };
};

const claimsApi = {
  getStatuses: () => apiClient.get("/claims/statuses").then((res) => res.data),

  getSummary: async (officeCode) => {
    const codes = parseOfficeCodes(officeCode);
    if (codes.length === 0) return null;
    if (codes.length === 1) {
      return apiClient.get("/claims/summary", claimOfficeHeader(codes[0])).then((res) => res.data);
    }
    const summaries = await Promise.all(
      codes.map((code) =>
        apiClient.get("/claims/summary", claimOfficeHeader(code)).then((res) => res.data)
      )
    );
    return aggregateSummaries(summaries);
  },

  searchClaims: async (searchRequest, { page = 0, size = 50, officeCode, signal } = {}) => {
    const codes = parseOfficeCodes(officeCode);
    if (codes.length === 0) {
      return { content: [], totalElements: 0, totalPages: 0 };
    }
    if (codes.length === 1) {
      const data = await apiClient
        .post(`/claim-details/search?page=${page}&size=${size}`, searchRequest, {
          ...claimOfficeHeader(codes[0]),
          signal,
        })
        .then((res) => res.data);
      return {
        ...data,
        content: (data?.content || []).map((claim) => ({ ...claim, officeCode: codes[0] })),
      };
    }
    const pages = await Promise.all(
      codes.map((code) =>
        apiClient
          .post(`/claim-details/search?page=0&size=${Math.max(size, 100)}`, searchRequest, {
            ...claimOfficeHeader(code),
            signal,
          })
          .then((res) => res.data)
      )
    );
    return mergeClaimPages(pages, codes, {
      sortBy: searchRequest?.sortBy,
      ascending: searchRequest?.ascending,
      page,
      size,
    });
  },

  getClaim: (claimNumber, officeCode) =>
    apiClient
      .get(`/claims/${encodeURIComponent(claimNumber)}`, claimOfficeHeader(officeCode))
      .then((res) => res.data),

  getClaimsByShipment: async (shipmentNumber, officeCode) => {
    try {
      const response = await apiClient.get(
        `/shipments/${encodeURIComponent(shipmentNumber)}/claims`,
        claimOfficeHeader(officeCode)
      );
      return response.data;
    } catch (error) {
      if (error?.response?.status !== 404) {
        throw error;
      }
      const searchResult = await claimsApi.searchClaims(
        { query: shipmentNumber, sortBy: "dateFiled", ascending: false },
        { page: 0, size: 100, officeCode }
      );
      return (searchResult?.content || []).filter(
        (claim) => claim.shipmentNumber === shipmentNumber
      );
    }
  },

  createClaim: (shipmentNumber, payload, officeCode) =>
    apiClient
      .post(`/claims?shipmentNumber=${encodeURIComponent(shipmentNumber)}`, payload, {
        ...claimOfficeHeader(officeCode),
      })
      .then((res) => res.data),

  /** @deprecated Prefer createClaim — shipment-scoped route may not be deployed. */
  createClaimForShipment: (shipmentNumber, payload, officeCode) =>
    claimsApi.createClaim(shipmentNumber, payload, officeCode),

  updateClaim: (claimNumber, payload, officeCode) =>
    apiClient
      .put(`/claims/${encodeURIComponent(claimNumber)}`, payload, claimOfficeHeader(officeCode))
      .then((res) => res.data),

  submitClaim: (claimNumber, officeCode) =>
    apiClient
      .post(
        `/claims/${encodeURIComponent(claimNumber)}/submit`,
        {},
        claimOfficeHeader(officeCode)
      )
      .then((res) => res.data),

  getClaimDocuments: (claimNumber, officeCode) =>
    apiClient
      .get(`/claims/${encodeURIComponent(claimNumber)}/documents`, claimOfficeHeader(officeCode))
      .then((res) => res.data),

  uploadClaimDocument: (claimNumber, file, officeCode) => {
    const resolvedOffice = resolveClaimOfficeCode({ claimOfficeCode: officeCode, officeCode });
    if (!resolvedOffice) {
      return Promise.reject(new Error("Office code is required to upload claim documents."));
    }

    const formData = new FormData();
    formData.append("file", file);

    return apiClient
      .post(`/claims/${encodeURIComponent(claimNumber)}/documents`, formData, {
        headers: {
          "Office-Code": resolvedOffice,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => res.data);
  },

  deleteClaimDocument: (claimNumber, documentUuid, officeCode) =>
    apiClient.post(
      `/claims/${encodeURIComponent(claimNumber)}/documents/${encodeURIComponent(documentUuid)}/delete`,
      {},
      claimOfficeHeader(officeCode)
    ),

  searchShipments: (query, officeCode, { page = 0, size = 20, signal } = {}) => {
    const q = String(query ?? "").trim();
    if (!q) {
      return Promise.resolve({ content: [], totalElements: 0 });
    }
    return apiClient
      .post(
        `/shipment-details/partial-search?page=${page}&size=${size}`,
        {
          query: q,
          sortBy: "shipmentNumber",
          filterType: "ALL",
          ascending: true,
          status: null,
        },
        {
          headers: {
            "Office-Code": getShipmentSearchOfficeHeader(officeCode),
          },
          signal,
        }
      )
      .then((res) => res.data);
  },

  getShipmentDetails: (shipmentNumber, officeCode) => {
    const codes = parseOfficeCodes(officeCode);
    const tryOrder =
      codes.length > 0 ? codes : [resolveClaimOfficeCode({ officeCode })];

    const attempt = async (index = 0) => {
      if (index >= tryOrder.length) {
        throw new Error("Unable to load shipment details for the selected office.");
      }
      const code = tryOrder[index];
      try {
        const response = await apiClient.get(
          `/shipments/detailed-shipment/${encodeURIComponent(shipmentNumber)}`,
          claimOfficeHeader(code)
        );
        return response.data;
      } catch (error) {
        if ([403, 404].includes(error?.response?.status) && index < tryOrder.length - 1) {
          return attempt(index + 1);
        }
        throw error;
      }
    };

    return attempt();
  },
};

export { claimsApi };
